import { useRef, useState } from 'react';
import {
  Download,
  Upload,
  AlertTriangle,
  Lock,
  Unlock,
  Loader2,
  Shield,
} from 'lucide-react';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useVault } from '@/hooks/useVault';
import { useWorkspaceSync } from '@/hooks/useWorkspaceSync';
import { isEncryptedExport } from '@/lib/workspace/export';
import { passphraseStrength } from '@/lib/workspace/validate';
import type { HistoryMode } from '@/lib/workspace/types';

/** History, encryption, backup — embeddable in the settings modal. */
export function WorkspaceSettingsPanel() {
  const {
    settings,
    setHistoryMode,
    updateSettings,
    exportAll,
    exportEncrypted,
    importFile,
    projects,
    chats,
  } = useWorkspace();
  const {
    status: vaultStatus,
    hasVault,
    unlocked,
    supportsNip44,
    enableWithNip44,
    enableWithPassphrase,
    lock,
    disableEncryption,
    error: vaultError,
    clearError,
  } = useVault();
  const {
    busy: syncBusy,
    error: syncError,
    status: syncStatus,
    publish: syncPublish,
    pull: syncPull,
    getPublishPreview,
    formatBytes,
    localHasData,
    autoPullOffer,
    acceptAutoPullOffer,
    dismissAutoPullOffer,
  } = useWorkspaceSync({ enableAutoPull: true });

  const fileRef = useRef<HTMLInputElement>(null);
  const [importMode, setImportMode] = useState<'merge' | 'replace'>('merge');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [exportPass, setExportPass] = useState('');
  const [importPass, setImportPass] = useState('');
  const [needsImportPass, setNeedsImportPass] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const modes: { id: HistoryMode; label: string; hint: string }[] = [
    {
      id: 'local',
      label: 'Local only',
      hint: 'Save projects and chats in this browser (IndexedDB)',
    },
    {
      id: 'sync',
      label: 'Sync (encrypted)',
      hint: 'Local + opt-in publish/pull of NIP-44 sealed snapshot to your relays',
    },
    {
      id: 'ephemeral',
      label: 'Ephemeral',
      hint: 'Do not write new messages to disk (session memory only)',
    },
  ];

  const handleImport = async (file: File, pass?: string) => {
    setError(null);
    setStatus(null);
    try {
      const text = await file.text();
      const raw = JSON.parse(text) as unknown;
      if (isEncryptedExport(raw) && !pass) {
        setPendingFile(file);
        setNeedsImportPass(true);
        return;
      }
      await importFile(file, importMode, pass);
      setStatus(
        importMode === 'replace'
          ? 'Workspace replaced from file'
          : 'Imported and merged into workspace',
      );
      setNeedsImportPass(false);
      setPendingFile(null);
      setImportPass('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    }
  };

  const handleEnableNip44 = async () => {
    clearError();
    setBusy(true);
    setError(null);
    try {
      await enableWithNip44();
      setStatus('Local encryption enabled (NIP-44)');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable encryption');
    } finally {
      setBusy(false);
    }
  };

  const handleEnablePass = async () => {
    clearError();
    if (!passStrength.ok) {
      setError('Use a passphrase of at least 12 characters');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await enableWithPassphrase(passphrase);
      setPassphrase('');
      setStatus('Local encryption enabled (passphrase)');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable encryption');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (
      !confirm(
        'Disable local encryption?\n\nExport an encrypted backup first. Data will be stored in plaintext on this device.',
      )
    ) {
      return;
    }
    const typed = window.prompt('Type DISABLE to confirm turning off encryption:');
    if (typed !== 'DISABLE') {
      setError('Disable cancelled — type DISABLE exactly to confirm');
      return;
    }
    setBusy(true);
    try {
      await disableEncryption();
      setStatus('Encryption disabled');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disable');
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async () => {
    try {
      const preview = await getPublishPreview();
      const lines = [
        'Publish encrypted workspace snapshot to your relays?',
        '',
        `Projects: ${preview.projects}`,
        `Chats: ${preview.chats}`,
        `Messages: ${preview.messages}${preview.excludeMessages ? ' (excluded by setting)' : ''}`,
        `Providers (no keys): ${preview.providers}`,
        `Approx size: ${formatBytes(preview.approxBytes)}`,
        '',
        'API keys are never included. Relays see that your npub uses OpenDork (kind + size metadata).',
      ];
      if (preview.warnSize || preview.warnMessages) {
        lines.push(
          '',
          '⚠ Large snapshot — consider “Exclude messages from sync” or pruning old chats.',
        );
      }
      if (!confirm(lines.join('\n'))) return;
      await syncPublish();
      setStatus(
        'Published encrypted snapshot to relays. On the other device: Pull & replace.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publish failed');
    }
  };

  const handlePullReplace = async () => {
    const localNote = localHasData
      ? `\n\nThis browser has ${projects.length} project(s) and ${chats.length} chat(s). Replace will OVERWRITE them with the remote snapshot.`
      : '';
    if (
      !confirm(
        `Replace local workspace with the latest OpenDork snapshot from relays?${localNote}\n\nExport a backup first if unsure.`,
      )
    ) {
      return;
    }
    try {
      const r = await syncPull('replace');
      setStatus(
        r.imported
          ? `Pulled workspace (${r.stats?.projects ?? '?'} projects, ${r.stats?.messages ?? '?'} messages). Re-enter API keys under Providers.`
          : 'No remote snapshot found. Publish from the device that has your data first.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Pull failed');
    }
  };

  const passStrength = passphraseStrength(passphrase);
  const exportPassStrength = passphraseStrength(exportPass);
  const idleMins = settings.idleLockMinutes ?? 30;

  const vaultLabel = !hasVault
    ? 'Off'
    : unlocked
      ? 'Unlocked'
      : vaultStatus === 'locked'
        ? 'Locked'
        : vaultStatus;

  return (
    <div className="space-y-6">
          <p className="text-[12px] text-[#5a5a5a] leading-relaxed">
            Control how history is stored, encrypt data at rest, and export backups. The vault
            protects IndexedDB when locked — an unlocked tab can still be read by XSS or
            extensions.
          </p>

          {/* History */}
          <div>
            <h3 className="text-sm font-medium text-[#e8e6e3] mb-2">History</h3>
            <div className="space-y-2">
              {modes.map((m) => (
                <button
                  key={m.id}
                  onClick={() => void setHistoryMode(m.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                    settings.historyMode === m.id
                      ? 'border-[#3a3a3a] bg-[#1a1a1a]'
                      : 'border-[#2a2a2a] hover:border-[#3a3a3a]'
                  }`}
                >
                  <div className="text-xs text-[#e8e6e3]">{m.label}</div>
                  <div className="text-[10px] text-[#5a5a5a] mt-0.5">{m.hint}</div>
                </button>
              ))}
            </div>
            {settings.historyMode === 'sync' && (
              <div className="mt-2 px-3 py-3 rounded-lg border border-[#2a2a2a] bg-[#0a0a0a] space-y-2.5">
                {autoPullOffer && unlocked && (
                  <div className="px-3 py-2.5 rounded-lg border border-sky-900/40 bg-sky-950/20 space-y-2">
                    <p className="text-[11px] text-sky-300/90 font-medium">
                      Restore from relays?
                    </p>
                    <p className="text-[10px] text-[#8a8884] leading-relaxed">
                      This browser has no projects. Look for an encrypted OpenDork snapshot on
                      your relays and restore it here? You can also skip and use Pull manually
                      later.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={syncBusy}
                        onClick={() => {
                          void acceptAutoPullOffer()
                            .then((r) =>
                              setStatus(
                                r.imported
                                  ? `Restored workspace (${r.stats?.projects ?? '?'} projects). Re-enter API keys under Providers.`
                                  : 'No remote snapshot found. Publish from the device that has your data first.',
                              ),
                            )
                            .catch((e) =>
                              setError(e instanceof Error ? e.message : 'Pull failed'),
                            );
                        }}
                        className="flex-1 py-1.5 text-[11px] bg-[#e8e6e3] text-[#0a0a0a] rounded-lg font-medium disabled:opacity-50"
                      >
                        {syncBusy ? '…' : 'Restore'}
                      </button>
                      <button
                        type="button"
                        disabled={syncBusy}
                        onClick={() => {
                          void dismissAutoPullOffer().then(() =>
                            setStatus('Skipped auto-restore. Use Pull when ready.'),
                          );
                        }}
                        className="flex-1 py-1.5 text-[11px] border border-[#2a2a2a] rounded-lg text-[#8a8884] disabled:opacity-50"
                      >
                        Not now
                      </button>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-[#8a8884] leading-relaxed font-medium">
                  Two-step sync — enable is not enough
                </p>
                <ol className="text-[10px] text-[#5a5a5a] leading-relaxed list-decimal list-inside space-y-1">
                  <li>
                    On the device <span className="text-[#8a8884]">with your data</span>: unlock
                    vault → <span className="text-[#c8c6c3]">Publish now</span>
                  </li>
                  <li>
                    On a <span className="text-[#8a8884]">new browser / deploy</span>: log in →
                    enable vault (NIP-44) → enable Sync →{' '}
                    <span className="text-[#c8c6c3]">Pull &amp; replace</span> (auto-pulls if
                    this device is empty)
                  </li>
                </ol>
                <p className="text-[10px] text-[#3a3a3a] leading-relaxed">
                  Snapshot is NIP-44 sealed to you (kind 39987). Includes projects, chats,
                  messages (unless excluded), global instructions, and provider base URLs —{' '}
                  <span className="text-[#5a5a5a]">not API keys</span>. Relays see your npub
                  published an OpenDork snapshot (kind + approximate size). Prefer relays you
                  trust. Compromised nsec can decrypt historical snapshots still on relays.
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!settings.syncExcludeMessages}
                    onChange={(e) =>
                      void updateSettings({ syncExcludeMessages: e.target.checked })
                    }
                    className="mt-0.5 rounded border-[#2a2a2a]"
                  />
                  <span className="text-[11px] text-[#5a5a5a] leading-relaxed">
                    Exclude messages from sync (projects, instructions, chat titles only —
                    smaller & less sensitive)
                  </span>
                </label>
                {!hasVault && (
                  <p className="text-[10px] text-yellow-500/80 leading-relaxed">
                    Enable local encryption below first — required for publish and pull.
                  </p>
                )}
                {hasVault && !unlocked && (
                  <p className="text-[10px] text-yellow-500/80 leading-relaxed">
                    Unlock local encryption before publish or pull.
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handlePublish()}
                    disabled={syncBusy || !unlocked}
                    className="flex-1 py-2 text-[11px] bg-[#e8e6e3] text-[#0a0a0a] rounded-lg font-medium disabled:opacity-50"
                  >
                    {syncBusy ? '…' : 'Publish now'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handlePullReplace()}
                    disabled={syncBusy || !unlocked}
                    className="flex-1 py-2 text-[11px] border border-[#2a2a2a] rounded-lg text-[#e8e6e3] disabled:opacity-50"
                  >
                    {syncBusy ? '…' : 'Pull & replace'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void syncPull(localHasData ? 'merge' : 'replace')
                      .then((r) =>
                        setStatus(
                          r.imported
                            ? localHasData
                              ? 'Merged remote snapshot into this browser'
                              : 'Pulled workspace from relays'
                            : 'No remote snapshot found',
                        ),
                      )
                      .catch((e) =>
                        setError(e instanceof Error ? e.message : 'Pull failed'),
                      );
                  }}
                  disabled={syncBusy || !unlocked}
                  className="w-full py-1.5 text-[10px] text-[#5a5a5a] hover:text-[#e8e6e3] disabled:opacity-40 transition-colors"
                >
                  {localHasData
                    ? 'Pull & merge (safer when this browser already has data)'
                    : 'Pull again'}
                </button>
                <div className="text-[10px] text-[#3a3a3a] space-y-0.5">
                  {(syncStatus.lastPublishedAt ?? settings.syncLastPublishedAt) ? (
                    <div>
                      Last publish:{' '}
                      {new Date(
                        syncStatus.lastPublishedAt ?? settings.syncLastPublishedAt!,
                      ).toLocaleString()}
                    </div>
                  ) : (
                    <div className="text-yellow-500/60">
                      Never published from this browser
                    </div>
                  )}
                  {(syncStatus.lastPulledAt ?? settings.syncLastPulledAt) ? (
                    <div>
                      Last pull:{' '}
                      {new Date(
                        syncStatus.lastPulledAt ?? settings.syncLastPulledAt!,
                      ).toLocaleString()}
                    </div>
                  ) : (
                    <div>Never pulled on this browser</div>
                  )}
                  {(syncError || syncStatus.lastError) && (
                    <div className="text-red-400/80">
                      {syncError || syncStatus.lastError}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Encryption */}
          <div>
            <h3 className="text-sm font-medium text-[#e8e6e3] mb-2 flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" />
              Local encryption
            </h3>
            <div className="px-3 py-2.5 rounded-lg border border-[#2a2a2a] bg-[#0a0a0a] mb-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#e8e6e3]">Status</span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    unlocked
                      ? 'bg-green-900/30 text-green-400/80'
                      : hasVault
                        ? 'bg-yellow-900/30 text-yellow-500/80'
                        : 'bg-[#1a1a1a] text-[#5a5a5a]'
                  }`}
                >
                  {vaultLabel}
                </span>
              </div>
              <p className="text-[10px] text-[#5a5a5a] mt-1.5 leading-relaxed">
                When on, project names, chat titles, message bodies, instructions, and context
                are AES-GCM encrypted in IndexedDB. The vault key is wrapped with NIP-44
                (encrypt-to-self) or a passphrase. Required for relay sync. While unlocked,
                data is in memory for the app to work — lock when stepping away.
              </p>
            </div>

            {hasVault && (
              <div className="mb-2 px-3 py-2.5 rounded-lg border border-[#2a2a2a] bg-[#0a0a0a] space-y-2">
                <label className="text-[11px] text-[#5a5a5a] block">
                  Auto-lock after idle
                </label>
                <select
                  value={idleMins}
                  onChange={(e) =>
                    void updateSettings({ idleLockMinutes: Number(e.target.value) })
                  }
                  className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] focus:outline-none"
                >
                  <option value={0}>Off</option>
                  <option value={5}>5 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes (default)</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                </select>
                <p className="text-[10px] text-[#3a3a3a]">
                  Locks the vault after inactivity. NIP-44 vaults unlock again via your signer.
                </p>
              </div>
            )}

            {!hasVault && (
              <div className="space-y-2">
                <div className="px-3 py-2.5 rounded-lg border border-yellow-900/40 bg-yellow-950/20 space-y-1">
                  <p className="text-[11px] text-yellow-500/90 font-medium">
                    Recommended before chatting
                  </p>
                  <p className="text-[10px] text-[#5a5a5a] leading-relaxed">
                    Without a vault, projects, chats, and API keys sit in plaintext IndexedDB on
                    this device. Enable NIP-44 encryption (one click with your signer) so data is
                    sealed at rest and relay sync can work.
                  </p>
                </div>
                {supportsNip44 && (
                  <button
                    onClick={() => void handleEnableNip44()}
                    disabled={busy}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-xs bg-[#e8e6e3] text-[#0a0a0a] rounded-lg font-medium disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
                    Enable with Nostr (NIP-44)
                  </button>
                )}
                <div className="space-y-1.5">
                  <div className="flex gap-2">
                    <input
                      type="password"
                      value={passphrase}
                      onChange={(e) => setPassphrase(e.target.value)}
                      placeholder="Passphrase (12+ recommended)"
                      className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none"
                    />
                    <button
                      onClick={() => void handleEnablePass()}
                      disabled={busy || !passStrength.ok}
                      className="px-3 py-2 text-xs border border-[#2a2a2a] rounded-lg text-[#e8e6e3] hover:border-[#3a3a3a] disabled:opacity-40"
                    >
                      Enable
                    </button>
                  </div>
                  {passphrase.length > 0 && (
                    <p
                      className={`text-[10px] ${
                        passStrength.score >= 3
                          ? 'text-green-400/80'
                          : passStrength.score >= 2
                            ? 'text-yellow-500/80'
                            : 'text-red-400/80'
                      }`}
                    >
                      {passStrength.label}
                    </p>
                  )}
                </div>
              </div>
            )}

            {hasVault && unlocked && (
              <div className="flex gap-2">
                <button
                  onClick={() => lock()}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs border border-[#2a2a2a] rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3]"
                >
                  <Lock className="w-3 h-3" />
                  Lock now
                </button>
                <button
                  onClick={() => void handleDisable()}
                  disabled={busy}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs border border-[#2a2a2a] rounded-lg text-red-400/70 hover:text-red-400"
                >
                  <Unlock className="w-3 h-3" />
                  Disable
                </button>
              </div>
            )}
          </div>

          {/* Export / Import */}
          <div>
            <h3 className="text-sm font-medium text-[#e8e6e3] mb-2">Export / Import</h3>
            <p className="text-[11px] text-[#5a5a5a] mb-3 leading-relaxed">
              Prefer encrypted bundles. API keys are never included. Plain JSON is readable by
              anyone with the file.
            </p>

            <div className="flex gap-2 mb-2">
              <input
                type="password"
                value={exportPass}
                onChange={(e) => setExportPass(e.target.value)}
                placeholder="Passphrase for encrypted export (12+)"
                className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none"
              />
              <button
                onClick={() => {
                  void exportEncrypted(exportPass)
                    .then(() => {
                      setStatus('Encrypted export downloaded');
                      setExportPass('');
                    })
                    .catch((err) =>
                      setError(err instanceof Error ? err.message : 'Export failed'),
                    );
                }}
                disabled={!exportPassStrength.ok}
                className="px-3 py-2 text-xs bg-[#e8e6e3] text-[#0a0a0a] rounded-lg font-medium hover:bg-white disabled:opacity-40"
              >
                Export encrypted
              </button>
            </div>
            {exportPass.length > 0 && (
              <p
                className={`text-[10px] mb-2 ${
                  exportPassStrength.score >= 3
                    ? 'text-green-400/80'
                    : exportPassStrength.score >= 2
                      ? 'text-yellow-500/80'
                      : 'text-red-400/80'
                }`}
              >
                {exportPassStrength.label}
              </p>
            )}

            <button
              onClick={() => {
                if (
                  !confirm(
                    'Export PLAIN JSON?\n\nChat history and instructions will be readable by anyone with the file. Prefer encrypted export.',
                  )
                ) {
                  return;
                }
                const typed = window.prompt('Type EXPORT to confirm plain (unencrypted) backup:');
                if (typed !== 'EXPORT') {
                  setError('Plain export cancelled');
                  return;
                }
                void exportAll().then(() => setStatus('Plain export downloaded'));
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 mb-2 text-xs text-[#5a5a5a] border border-[#2a2a2a] rounded-lg hover:text-[#e8e6e3] hover:border-[#3a3a3a] transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export plain JSON (dangerous)
            </button>

            <div className="flex gap-2 mb-2">
              {(['merge', 'replace'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setImportMode(m)}
                  className={`flex-1 py-1.5 text-[10px] rounded-md border ${
                    importMode === m
                      ? 'border-[#3a3a3a] text-[#e8e6e3] bg-[#1a1a1a]'
                      : 'border-[#2a2a2a] text-[#5a5a5a]'
                  }`}
                >
                  {m === 'merge' ? 'Merge on import' : 'Replace on import'}
                </button>
              ))}
            </div>

            {importMode === 'replace' && (
              <div className="flex gap-2 mb-2 p-2 bg-yellow-950/20 border border-yellow-900/30 rounded-lg">
                <AlertTriangle className="w-3.5 h-3.5 text-yellow-500/70 shrink-0 mt-0.5" />
                <p className="text-[10px] text-[#5a5a5a]">
                  Replace wipes current local projects/chats before loading the file.
                </p>
              </div>
            )}

            {needsImportPass && (
              <div className="mb-2 space-y-2 p-2 border border-[#2a2a2a] rounded-lg">
                <p className="text-[10px] text-[#5a5a5a]">Encrypted backup — enter passphrase</p>
                <input
                  type="password"
                  value={importPass}
                  onChange={(e) => setImportPass(e.target.value)}
                  placeholder="Passphrase"
                  className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] focus:outline-none"
                />
                <button
                  onClick={() => {
                    if (pendingFile) void handleImport(pendingFile, importPass);
                  }}
                  disabled={importPass.length < 8}
                  className="w-full py-2 text-xs bg-[#e8e6e3] text-[#0a0a0a] rounded-lg disabled:opacity-40"
                >
                  Decrypt & import
                </button>
              </div>
            )}

            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImport(file);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-xs text-[#5a5a5a] border border-[#2a2a2a] rounded-lg hover:text-[#e8e6e3] hover:border-[#3a3a3a] transition-colors"
            >
              <Upload className="w-3.5 h-3.5" />
              Import JSON…
            </button>

            {(status || vaultError) && (
              <p className="mt-2 text-[11px] text-green-400/80">{status || vaultError}</p>
            )}
            {error && (
              <p className="mt-2 text-[11px] text-red-400">{error}</p>
            )}
          </div>
    </div>
  );
}
